import type { ReactNode } from 'react';

/**
 * Zentrale Icon-Registry der Navigation (D-M0-K4). Eigene Inline-SVGs (keine
 * Icon-Bibliothek, Brief §10), Outline/Stroke, 20 px, einheitliches Gewicht.
 * Icon-Vertrag: jedes Symbol zeigt das Objekt (Beleg, Kunde, Zeit), nicht die
 * Handlung; jedes wird höchstens einmal in der Navigation vergeben. Der
 * Duplikat-Test (navModel.test.ts) sichert Eindeutigkeit gegen künftige
 * Milestones ab. `collapse`/`bell` bedienen die Topbar und sind keine
 * Navigationseinträge; `textBlock`/`bookmarkPlus`/`imagePlus` bedienen die
 * Auftragspositionen (Faruq 2026-08-24) — auch dort keine Icon-Bibliothek.
 */
export const ICON_PATHS: Record<string, ReactNode> = {
  // Übersicht: vier Kacheln (Raster) — etabliert.
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  // Angebote: Dokument mit Stift — Entwurf, noch verhandelbar.
  quotes: (
    <>
      <path d="M13.5 3.5H6.5a1 1 0 0 0-1 1v15a1 1 0 0 0 1 1h6" />
      <path d="M9 9h4.5M9 12.5h3" />
      <path d="M19.6 12.1l1.8 1.8-5 5H14.5v-1.9z" />
    </>
  ),
  // Aufträge: Klemmbrett mit Häkchenliste — die Arbeitsanweisung.
  // Tobias 2026-08-29: Materialbestellungen — ein Paket, klar gegen die
  // Klemmbrett-Form von „Aufträge" abgesetzt.
  // Faruq 2026-09-25: Bestellungen als Einkaufswagen (vorher Paket).
  material: (
    <>
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="18" cy="20" r="1.4" />
      <path d="M2.5 3.5h2.6l2.4 11.2a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.1l1.9-7.3H6.2" />
    </>
  ),
  orders: (
    <>
      <rect x="5" y="5" width="14" height="16" rx="2" />
      <rect x="9" y="3" width="6" height="3.4" rx="1.2" />
      <path d="M8.4 11.2l1.3 1.3 2.3-2.4" />
      <path d="M14.5 11h2.2" />
      <path d="M8.4 16.2l1.3 1.3 2.3-2.4" />
      <path d="M14.5 16h2.2" />
    </>
  ),
  // Plantafel: Kalender mit Wochenraster.
  plantafel: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 4v3M16 4v3M8 13h3M8 17h3" />
    </>
  ),
  // Wiederkehrend: Kreispfeile (Wiederholung).
  recurring: (
    <>
      <path d="M4 12a8 8 0 0 1 13.7-5.6L20 8M20 4v4h-4" />
      <path d="M20 12a8 8 0 0 1-13.7 5.6L4 16M4 20v-4h4" />
    </>
  ),
  // Belege: Dokument mit Textzeilen und geknickter Ecke — die Rechnung selbst.
  documents: (
    <>
      <path d="M6 3h8l5 5v13H6z" />
      <path d="M14 3v5h5" />
      <path d="M9 12.5h6M9 15.5h6M9 18.5h4" />
    </>
  ),
  // Offene Posten: Geldschein mit Münze — Forderungen, nicht Zeit.
  openItems: (
    <>
      <rect x="3" y="6.5" width="18" height="11" rx="2" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M6.5 9.8v4.4M17.5 9.8v4.4" />
    </>
  ),
  // Zahlungseingänge: Bankgebäude mit Pfeil hinein — Geld kommt vom Konto
  // herein. Grenzt gegen „Offene Posten" (Schein+Münze = Forderung) ab.
  bankIncoming: (
    <>
      <path d="M3.5 9.5L12 4.5l8.5 5" />
      <path d="M5.5 10.5v7M18.5 10.5v7M3.5 19.5h17" />
      <path d="M12 10.5v5.5M9.8 13.8L12 16l2.2-2.2" />
    </>
  ),
  // Mahnwesen: Glocke — Erinnerung (das Ausrufezeichen las sich wie ein Fehler).
  dunning: (
    <>
      <path d="M6 16v-5a6 6 0 1 1 12 0v5l1.6 2.5H4.4z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  // Arbeitszeit: Uhr — die Uhr gehört exklusiv hierher.
  attendance: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.6" />
    </>
  ),
  // Auswertungen: Balkendiagramm.
  analytics: (
    <>
      <path d="M4 4v16h16" />
      <path d="M8 16v-4M12 16v-8M16 16v-6" />
    </>
  ),
  // Aktivitäten: Puls-/Aktivitätslinie — der laufende Betrieb, kein Diagramm.
  activity: (
    <>
      <path d="M3 12h4l2.5-6 4 13 2.5-7H21" />
    </>
  ),
  // Kunden: zwei Personen.
  customers: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3.5 20c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16 8.5a3 3 0 1 0 0 .01M17 15c2.2.4 3.5 2 3.5 5" />
    </>
  ),
  // Artikel: Würfel/Paket.
  articles: (
    <>
      <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" />
      <path d="M4 7.5l8 4.5 8-4.5M12 12v9" />
    </>
  ),
  // Preislisten: Preisschild/Etikett — Preis, nicht Dokument.
  priceLists: (
    <>
      <path d="M3.6 12.6l8.4-8.4a1.6 1.6 0 0 1 1.1-.5h5.2a1.6 1.6 0 0 1 1.6 1.6v5.2a1.6 1.6 0 0 1-.5 1.1l-8.4 8.4a1.6 1.6 0 0 1-2.3 0l-5.1-5.1a1.6 1.6 0 0 1 0-2.3z" />
      <circle cx="16.3" cy="7.7" r="1.3" fill="currentColor" stroke="none" />
    </>
  ),
  // Kundenart Privat: einzelne Person (vor dem Namen in der Kundenliste).
  personSingle: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
    </>
  ),
  // Kundenart Firma: Gebäude mit Fenstern und Tür.
  building: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1.4" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2" />
      <path d="M10 21v-3.2h4V21" />
    </>
  ),
  // Benutzer: Person mit Zahnrad — grenzt gegen Kunden (zwei Personen) ab.
  users: (
    <>
      <circle cx="10" cy="7.5" r="3.3" />
      <path d="M4 20c0-3.3 2.7-6 6-6 .8 0 1.6.16 2.3.45" />
      <circle cx="17.5" cy="16.5" r="2.3" />
      <path d="M17.5 12.9v1.3M17.5 18.8v1.3M13.9 16.5h1.3M19.7 16.5h1.3M15 14l.9.9M19.1 18.1l.9.9M20 14l-.9.9M15 19l.9-.9" />
    </>
  ),
  // Geräte (P2-M12): Tablet im Hochformat mit Standtaste — das gekoppelte
  // Monteur-App-Gerät (grenzt gegen das breite „Offene Posten"-Rechteck ab).
  devices: (
    <>
      <rect x="6" y="2.5" width="12" height="19" rx="2.2" />
      <path d="M10.5 18.5h3" />
    </>
  ),
  // Rollen: Schlüssel — Rollen sind die Rechte-Vorlagen der Zugänge.
  roles: (
    <>
      <circle cx="8" cy="8.5" r="4.2" />
      <circle cx="8" cy="8.5" r="1.4" />
      <path d="M11.2 11.6 20 20.4M16.4 16.8l2.4-2.4M13.6 14l1.9-1.9" />
    </>
  ),
  // Einstellungen: Zahnrad mit Nabenring und Zähnen am Rand — ersetzt das alte
  // Sonnen-Symbol (der Ring unterscheidet das Zahnrad von der Sonne).
  settings: (
    <>
      <circle cx="12" cy="12" r="2.6" />
      <circle cx="12" cy="12" r="5.8" />
      <path d="M12 2.7v3.5M12 17.8v3.5M21.3 12h-3.5M6.2 12H2.7M18.6 5.4l-2.5 2.5M8 16l-2.6 2.6M18.6 18.6l-2.5-2.5M8 8L5.4 5.4" />
    </>
  ),
  // Datenaustausch: zwei gegenläufige Pfeile — Import und Export in einem.
  exchange: (
    <>
      <path d="M4 8h13M13 4l4 4-4 4" />
      <path d="M20 16H7M11 20l-4-4 4-4" />
    </>
  ),
  // ── Stempeluhr-Aktionen (TICKET P3-A2, keine Navigationseinträge) ──────────
  // Kommen: Play (Dreieck).
  clockPlay: <path d="M8 5.5v13l11-6.5z" />,
  // Pause: zwei senkrechte Balken.
  clockPause: (
    <>
      <rect x="7.5" y="5.5" width="3.4" height="13" rx="1.2" />
      <rect x="13.1" y="5.5" width="3.4" height="13" rx="1.2" />
    </>
  ),
  // Pause beenden: Play mit Rücksprung-Bogen (nicht identisch mit Kommen).
  clockResume: (
    <>
      <path d="M6.5 10.5a6 6 0 0 1 10-2" />
      <path d="M17 4.5v4h-4" />
      <path d="M9.5 11v6l5-3z" />
    </>
  ),
  // Gehen: Stop (Quadrat).
  clockStop: <rect x="6.5" y="6.5" width="11" height="11" rx="2" />,
  // ── Topbar (keine Navigationseinträge) ────────────────────────────────────
  collapse: <path d="M4 6h16M4 12h16M4 18h16" />,
  // Suche (P5-A §3): Lupe — bedient den Topbar-Auslöser der globalen Suche,
  // kein Navigationseintrag.
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M16.5 16.5L21 21" />
    </>
  ),
  // Chevron (Aufklapp-Indikator, D-W1 Einstellungen-Muster): zeigt nach
  // rechts; „aufgeklappt" entsteht per CSS-Rotation am Verwendungsort.
  chevron: <path d="M9 6l6 6-6 6" />,
  bell: (
    <>
      <path d="M6 16v-5a6 6 0 1 1 12 0v5l1.5 2.5H4.5z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  // ── Nutzer-Menü am Sidebar-Fuß (keine Navigationseinträge) ─────────────────
  // Hilfe: großes Fragezeichen im Kreis. Der Punkt ist ein gefüllter Kreis
  // (kein h.01-Strich, der wie ein Unterstrich wirkt).
  help: (
    <>
      <circle cx="12" cy="12" r="9.2" />
      <path d="M8.9 9a3.2 3.2 0 0 1 6.1 1.1c0 2.1-3 2.4-3 4.3" />
      <circle cx="12" cy="17.4" r="0.7" fill="currentColor" stroke="none" />
    </>
  ),
  // Support: Rettungsring — greifbare Unterstützung.
  support: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="M4.6 6.8l4.4 4.4M15 15l4.4 4.4M17.2 4.6l-4.4 4.4M9 15l-4.4 4.4" />
    </>
  ),
  // Abmelden: Tür mit hinausweisendem Pfeil.
  logout: (
    <>
      <path d="M15 4.5H6.5a1.5 1.5 0 0 0-1.5 1.5v12a1.5 1.5 0 0 0 1.5 1.5H15" />
      <path d="M11 12h9M16.5 8.5l3.5 3.5-3.5 3.5" />
    </>
  ),

  // Textbaustein: Absatzblock — der fertige Text, der eingesetzt wird.
  textBlock: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M7 9h10M7 12.5h10M7 16h6" />
    </>
  ),
  // Baustein sichern: Lesezeichen mit Plus — Text für später merken.
  bookmarkPlus: (
    <>
      <path d="M6.5 4.5h11a1 1 0 0 1 1 1v14.6l-6.5-4.4-6.5 4.4V5.5a1 1 0 0 1 1-1z" />
      <path d="M12 8.2v4.2M9.9 10.3h4.2" />
    </>
  ),
  // Tickets: Sprechblase mit Zeilen — eine Meldung, auf die geantwortet wird.
  ticket: (
    <>
      <path d="M4 5.5h16a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5h-8.5L7 20.5V16.5H4A1.5 1.5 0 0 1 2.5 15V7A1.5 1.5 0 0 1 4 5.5z" />
      <path d="M6.5 9.5h11M6.5 12.5h7" />
    </>
  ),
  // Bearbeiten: Stift. Zeilenaktion in Tabellen.
  edit: (
    <>
      <path d="M4 20h4.2l9.4-9.4a2.1 2.1 0 0 0 0-3l-1.2-1.2a2.1 2.1 0 0 0-3 0L4 15.8z" />
      <path d="M13.8 6.6l3.6 3.6" />
    </>
  ),
  // Löschen: Kreuz. Zeilenaktion in Tabellen, immer in Rot.
  remove: (
    <>
      <path d="M6 6l12 12M18 6L6 18" />
    </>
  ),
  // Faruq 2026-10-30: Mülleimer — Bestellung entfernen.
  trash: (
    <>
      <path d="M4 7h16M9 7V4.8h6V7" />
      <path d="M6.5 7l.9 12.2a1.6 1.6 0 0 0 1.6 1.5h6a1.6 1.6 0 0 0 1.6-1.5L17.5 7" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  // Bild anhängen: Bildrahmen mit Plus.
  imagePlus: (
    <>
      <path d="M12.5 20H6a2.5 2.5 0 0 1-2.5-2.5v-11A2.5 2.5 0 0 1 6 4h11a2.5 2.5 0 0 1 2.5 2.5V13" />
      <circle cx="9" cy="9.4" r="1.3" />
      <path d="M4 16.6l3.6-3.4a1.8 1.8 0 0 1 2.5 0L13 16.2" />
      <path d="M15.8 18.6h5.4M18.5 15.9v5.4" />
    </>
  ),
};

/** Icon-Namen der vier Stempeluhr-Aktionen (Duplikat-Test, TICKET P3-A2). */
export const CLOCK_ACTION_ICONS = ['clockPlay', 'clockPause', 'clockResume', 'clockStop'] as const;

/** Minimale geometrische Inline-Icons (Standard 20 px, stroke; keine Bibliothek). */
export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
